import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { alpacaService, Order } from '../../services/AlpacaService';
import { isOptionSymbol } from '../../utils/symbolUtils';
import { useAccountMode } from '../../context/AccountModeContext';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import PnlChart from '../../components/PnlChart/PnlChart';
import styles from './Trades.module.css';

type FilterStatus = 'all' | 'filled' | 'open' | 'canceled';
type DatePreset = 'today' | '7d' | '30d' | '90d' | 'all' | 'custom';

function getDatePresetRange(preset: DatePreset): { after?: string; until?: string } {
  if (preset === 'all') return {};
  const now = new Date();
  const until = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1); // end of today
  let start: Date;
  switch (preset) {
    case 'today':
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case '7d':
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    default:
      return {};
  }
  return {
    after: start.toISOString(),
    until: until.toISOString(),
  };
}

interface SymbolPerformance {
  symbol: string;
  roundTrips: number;
  wins: number;
  losses: number;
  winRate: string;
  totalPnl: number;
  avgWin: number;
  avgLoss: number;
  profitFactor: string;
  bestTrade: number;
  worstTrade: number;
  totalQty: number;
}

function computeSymbolPerformance(orders: Order[]): SymbolPerformance | null {
  const filled = orders.filter((o) => o.status === 'filled' && o.filled_avg_price);
  if (filled.length === 0) return null;

  const symbol = filled[0].symbol;
  const buys = filled
    .filter((o) => o.side === 'buy')
    .sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime());
  const sells = filled
    .filter((o) => o.side === 'sell')
    .sort((a, b) => new Date(a.submitted_at).getTime() - new Date(b.submitted_at).getTime());

  const pairs = Math.min(buys.length, sells.length);
  if (pairs === 0) return null;

  let wins = 0;
  let losses = 0;
  let totalWinAmt = 0;
  let totalLossAmt = 0;
  let totalPnl = 0;
  let bestTrade = -Infinity;
  let worstTrade = Infinity;
  let totalQty = 0;

  for (let i = 0; i < pairs; i++) {
    const buyPrice = parseFloat(buys[i].filled_avg_price || '0');
    const sellPrice = parseFloat(sells[i].filled_avg_price || '0');
    const qty = Math.min(parseFloat(buys[i].filled_qty), parseFloat(sells[i].filled_qty));
    const pnl = (sellPrice - buyPrice) * qty;
    totalPnl += pnl;
    totalQty += qty;
    if (pnl > bestTrade) bestTrade = pnl;
    if (pnl < worstTrade) worstTrade = pnl;
    if (pnl > 0) {
      wins++;
      totalWinAmt += pnl;
    } else {
      losses++;
      totalLossAmt += Math.abs(pnl);
    }
  }

  const total = wins + losses;
  return {
    symbol,
    roundTrips: pairs,
    wins,
    losses,
    winRate: total > 0 ? ((wins / total) * 100).toFixed(1) : '0',
    totalPnl,
    avgWin: wins > 0 ? totalWinAmt / wins : 0,
    avgLoss: losses > 0 ? totalLossAmt / losses : 0,
    profitFactor: totalLossAmt > 0 ? (totalWinAmt / totalLossAmt).toFixed(2) : 'N/A',
    bestTrade: bestTrade === -Infinity ? 0 : bestTrade,
    worstTrade: worstTrade === Infinity ? 0 : worstTrade,
    totalQty,
  };
}

const Trades: React.FC = () => {
  const { mode } = useAccountMode();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [symbolFilter, setSymbolFilter] = useState('');
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        // Determine date range
        let after: string | undefined;
        let until: string | undefined;

        if (datePreset === 'custom') {
          if (startDate) after = new Date(startDate).toISOString();
          if (endDate) {
            // Set end date to end of the selected day
            const end = new Date(endDate);
            end.setDate(end.getDate() + 1);
            until = end.toISOString();
          }
        } else {
          const range = getDatePresetRange(datePreset);
          after = range.after;
          until = range.until;
        }

        const data = await alpacaService.getOrders(filter === 'open' ? 'open' : 'all', '500', after, until, mode);
        // Exclude options contracts and multi-leg orders (they have their own page)
        let filtered = data.orders.filter((o) => !isOptionSymbol(o.symbol) && o.order_class !== 'mleg');
        if (filter === 'filled') {
          filtered = filtered.filter((o) => o.status === 'filled');
        } else if (filter === 'canceled') {
          filtered = filtered.filter((o) => o.status === 'canceled' || o.status === 'cancelled');
        }
        setOrders(filtered);
        setError('');
      } catch (err: any) {
        setError(err.message || 'Failed to load orders');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [filter, datePreset, startDate, endDate, mode]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get unique symbols for the datalist
  const uniqueSymbols = Array.from(new Set(orders.map((o) => o.symbol))).sort();

  // Apply symbol filter
  const displayedOrders = symbolFilter
    ? orders.filter((o) => o.symbol.toUpperCase() === symbolFilter.toUpperCase())
    : orders;

  const statusClass = (status: string) => {
    if (status === 'filled') return styles.filled;
    if (status === 'new' || status === 'accepted' || status === 'partially_filled') return styles.open;
    return styles.canceled;
  };

  const exportCsv = () => {
    const headers = ['Date', 'Symbol', 'Side', 'Qty', 'Filled Qty', 'Price', 'Filled Price', 'Status', 'Type'];
    const rows = displayedOrders.map((o) => [
      o.submitted_at || '',
      o.symbol,
      o.side,
      o.qty,
      o.filled_qty || '',
      o.limit_price || o.stop_price || '',
      o.filled_avg_price || '',
      o.status,
      o.type,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `signaldeck-trades-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className={styles.page}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>💹 Trades</h1>
        {displayedOrders.length > 0 && (
          <button className={styles.exportBtn} onClick={exportCsv}>
            Export CSV
          </button>
        )}
      </div>

      <div className={styles.filterRow}>
        <div className={styles.filters}>
          {(['all', 'filled', 'open', 'canceled'] as FilterStatus[]).map((f) => (
            <button
              key={f}
              className={`${styles.filterBtn} ${filter === f ? styles.active : ''}`}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className={styles.symbolSearch}>
          <input
            type="text"
            placeholder="Filter by symbol..."
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value)}
            list="symbol-options"
            className={styles.symbolInput}
            title="Filter by symbol"
          />
          {symbolFilter && (
            <button className={styles.clearBtn} onClick={() => setSymbolFilter('')}>
              ✕
            </button>
          )}
          <datalist id="symbol-options">
            {uniqueSymbols.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.datePresets}>
          {(
            [
              ['today', 'Today'],
              ['7d', '7 Days'],
              ['30d', '30 Days'],
              ['90d', '90 Days'],
              ['all', 'All Time'],
              ['custom', 'Custom'],
            ] as [DatePreset, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              className={`${styles.presetBtn} ${datePreset === key ? styles.activePreset : ''}`}
              onClick={() => {
                setDatePreset(key);
                if (key !== 'custom') {
                  setStartDate('');
                  setEndDate('');
                }
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {datePreset === 'custom' && (
          <div className={styles.dateFilter}>
            <span className={styles.dateLabel}>From</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={styles.dateInput}
              title="Filter start date"
            />
            <span className={styles.dateLabel}>To</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={styles.dateInput}
              title="Filter end date"
            />
            {(startDate || endDate) && (
              <button
                className={styles.clearBtn}
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
              >
                ✕
              </button>
            )}
          </div>
        )}
      </div>

      {loading && <SignalLoader message="Loading orders..." />}
      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && (
        <>
          <PnlChart orders={displayedOrders} />
          <p className={styles.count}>
            {displayedOrders.length} orders{symbolFilter ? ` for ${symbolFilter.toUpperCase()}` : ''}
          </p>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Filled</th>
                  <th>Fill Price</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Filled At</th>
                </tr>
              </thead>
              <tbody>
                {displayedOrders.map((order) => (
                  <tr key={order.id}>
                    <td className={styles.symbol}>{order.symbol}</td>
                    <td className={order.side === 'buy' ? styles.buy : styles.sell}>{order.side.toUpperCase()}</td>
                    <td>{order.type}</td>
                    <td>{order.qty}</td>
                    <td>{order.filled_qty}</td>
                    <td>{order.filled_avg_price ? `$${parseFloat(order.filled_avg_price).toFixed(2)}` : '—'}</td>
                    <td>
                      <span className={statusClass(order.status)}>{order.status}</span>
                    </td>
                    <td>{formatDate(order.submitted_at)}</td>
                    <td>{formatDate(order.filled_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className={styles.mobileCards}>
            {displayedOrders.map((order) => (
              <div key={order.id} className={styles.mobileCard}>
                <div className={styles.mobileCardHeader}>
                  <span className={styles.symbol}>{order.symbol}</span>
                  <span className={statusClass(order.status)}>{order.status}</span>
                </div>
                <div className={styles.mobileCardBody}>
                  <div className={styles.mobileCardRow}>
                    <span>Side</span>
                    <span className={order.side === 'buy' ? styles.buy : styles.sell}>{order.side.toUpperCase()}</span>
                  </div>
                  <div className={styles.mobileCardRow}>
                    <span>Type</span>
                    <span>{order.type}</span>
                  </div>
                  <div className={styles.mobileCardRow}>
                    <span>Qty</span>
                    <span>
                      {order.filled_qty}/{order.qty}
                    </span>
                  </div>
                  <div className={styles.mobileCardRow}>
                    <span>Fill Price</span>
                    <span>{order.filled_avg_price ? `$${parseFloat(order.filled_avg_price).toFixed(2)}` : '—'}</span>
                  </div>
                  <div className={styles.mobileCardRow}>
                    <span>Submitted</span>
                    <span>{formatDate(order.submitted_at)}</span>
                  </div>
                  {order.filled_at && (
                    <div className={styles.mobileCardRow}>
                      <span>Filled</span>
                      <span>{formatDate(order.filled_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {symbolFilter &&
            (() => {
              const perf = computeSymbolPerformance(displayedOrders);
              if (!perf) return null;
              const fmt = (n: number) =>
                n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
              const pnlCls = (n: number) => (n > 0 ? styles.positive : n < 0 ? styles.negative : '');
              return (
                <div className={styles.perfSection}>
                  <h2 className={styles.perfTitle}>{perf.symbol} Performance</h2>
                  <div className={styles.perfGrid}>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Total P&L</span>
                      <span className={`${styles.perfValue} ${pnlCls(perf.totalPnl)}`}>${fmt(perf.totalPnl)}</span>
                    </div>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Win Rate</span>
                      <span className={styles.perfValue}>{perf.winRate}%</span>
                    </div>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Round Trips</span>
                      <span className={styles.perfValue}>{perf.roundTrips}</span>
                    </div>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Profit Factor</span>
                      <span className={styles.perfValue}>{perf.profitFactor}</span>
                    </div>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Wins / Losses</span>
                      <span className={styles.perfValue}>
                        <span className={styles.positive}>{perf.wins}</span>
                        {' / '}
                        <span className={styles.negative}>{perf.losses}</span>
                      </span>
                    </div>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Avg Win / Avg Loss</span>
                      <span className={styles.perfValue}>
                        <span className={styles.positive}>${fmt(perf.avgWin)}</span>
                        {' / '}
                        <span className={styles.negative}>${fmt(perf.avgLoss)}</span>
                      </span>
                    </div>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Best Trade</span>
                      <span className={`${styles.perfValue} ${pnlCls(perf.bestTrade)}`}>${fmt(perf.bestTrade)}</span>
                    </div>
                    <div className={styles.perfCard}>
                      <span className={styles.perfLabel}>Worst Trade</span>
                      <span className={`${styles.perfValue} ${pnlCls(perf.worstTrade)}`}>${fmt(perf.worstTrade)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
        </>
      )}

      <Link to="/home" className={styles.back}>
        ← Back to Home
      </Link>
    </section>
  );
};

export default Trades;
